// FILE: src/features/nmck-calculator/lib/gbr.ts
// VERSION: 1.1.0
// START_MODULE_CONTRACT:
// PURPOSE: Рыночная методика расчёта услуги «Пультовая охрана с ГБР» (группа быстрого реагирования).
// SCOPE: Чистые функции без зависимостей: тарифная таблица ГБР по типу объекта, расчёт итога
//   (абонплата × срок, опция вооружённой ГБР ×1.3), точный срок контракта (месяцы + дни),
//   тарифицируемый срок (billableMonths) и восстановление срока из billableMonths.
// INPUT: Тип объекта / абонплата / срок / признак вооружённой ГБР; даты контракта.
// OUTPUT: Итоговая стоимость ГБР за период; срок контракта {months, days, billableMonths}; label срока.
// KEYWORDS: DOMAIN(NMCC): GbrOption; DOMAIN(Market): QuickResponseTeam; MATH(Formula): Subscription; TECH(TS): PureModule
// LINKS: REFERENCES(https://oxpa.pro/wiki/gruppa-bystrogo-reagirovaniya), CONCEPT(revive-plan §3 Ф4)
// END_MODULE_CONTRACT
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: [1.1.0] — делитель дней 30.5 (≈365/12, решение владельца 2026-08-22): billableMonths =
//   max(1, months + days/30.5). billedAtMinimum в GbrDuration: срок < 1 мес поднят клампом до 1 —
//   label показываем честный («26 дн.») с пометкой «(минимум — 1 мес)». gbrDurationFromBillable
//   пересчитан под 30.5 (months = floor(billable), days = round((billable-months)*30.5), days>30 → +1 мес).
// END_CHANGE_SUMMARY

// <MEMENTO>2026-08-21: ГБР — РЫНОЧНАЯ опция и НЕ входит в методику Приказа Росгвардии №45
// (расчёт НМЦК). Тарифы взяты из собственной вики oxpa.pro/wiki/gruppa-bystrogo-reagirovaniya
// (секция «Стоимость ГБР в 2026 году»): 6 типов объектов, «от N ₽/мес». В калькуляторе используется
// нижняя граница тарифа (от …), пользователь может отредактировать абонплату вручную.
// Вооружённая ГБР — надбавка 20–40% по вики; берём середину 30% (GBR_ARMED_COEF = 1.3).
// НЕ «чинить» под Приказ 45 при рефакторинге — это отдельная строка сметы, добавляется
// к НМЦК сверху, но не подменяет её.</MEMENTO>

export interface GbrObjectType {
  key: string;
  label: string;
  defaultFeeMonthly: number;
}

// Тарифная таблица ГБР (вики oxpa.pro/wiki/gruppa-bystrogo-reagirovaniya, секция «Стоимость»)
export const GBR_OBJECT_TYPES: GbrObjectType[] = [
  { key: 'shop', label: 'Магазин / торговый павильон', defaultFeeMonthly: 5000 },
  { key: 'office', label: 'Офис / бизнес-центр', defaultFeeMonthly: 7000 },
  { key: 'warehouse', label: 'Склад / производство', defaultFeeMonthly: 15000 },
  { key: 'construction', label: 'Строительная площадка', defaultFeeMonthly: 20000 },
  { key: 'bank', label: 'Банк / инкассация', defaultFeeMonthly: 35000 },
  { key: 'industrial', label: 'Промышленное предприятие', defaultFeeMonthly: 60000 },
];

// Надбавка за вооружённую ГБР (6 разряд): по вики 20–40%, берём середину 30% (решение 2026-08-21)
export const GBR_ARMED_COEF = 1.3;

export interface GbrCalcInput {
  feeMonthly: number;
  months: number;
  armed: boolean;
}

/**
 * Итоговая стоимость ГБР за период: абонплата × срок (× 1.3 для вооружённой ГБР).
 * Округление до копеек (2 знака) — деньги в рублях.
 */
export function calcGbrTotal({ feeMonthly, months, armed }: GbrCalcInput): number {
  const fee = Number.isFinite(feeMonthly) ? feeMonthly : 0;
  const m = Number.isFinite(months) && months > 0 ? months : 1;
  const total = fee * m * (armed ? GBR_ARMED_COEF : 1);
  return Math.round(total * 100) / 100;
}

// <MEMENTO>2026-08-22: contractDuration считает ЦЕЛЫЕ календарные месяцы + остаток дней
// (без округления вверх — тарификация дней пропорционально days/30.5, решение владельца).
// addMonthsClamped зажимает день месяца к последнему дню целевого месяца (31.01 → 28.02
// в невисокосный год), иначе граница месяца «уезжает» через нормализацию Date
// и 31.01→02.03 посчитался бы как 1 месяц вместо 2.</MEMENTO>
function addMonthsClamped(date: Date, months: number): Date {
  const day = date.getDate();
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDay));
  target.setHours(0, 0, 0, 0);
  return target;
}

export interface GbrDuration {
  /** Целые календарные месяцы срока */
  months: number;
  /** Остаток дней после границы последнего целого месяца */
  days: number;
  /** Тарифицируемый срок в месяцах (для calcGbrTotal) */
  billableMonths: number;
  /** true — фактический срок < 1 мес, billableMonths поднят клампом до 1 */
  billedAtMinimum: boolean;
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

// <MEMENTO>2026-08-22 (решение владельца, уточнение того же дня): округление срока ГБР вверх
// НЕ используется — тарификация дней пропорционально через СРЕДНИЙ месяц 30.5 дней (≈365/12):
// billableMonths = max(1, months + days/30.5) с округлением до 4 знаков. В календарных месяцах
// разное число дней (28–31), 30.5 не искажает усреднённую рыночную абонплату ГБР.
// К НМЦК-части (зарплата/часы) это НЕ относится — там точный почасовой расчёт (RPC), не трогать.
// min 1 мес сохранён: срок < 1 мес тарифицируется как один месяц (защита от нулевой суммы);
// при этом billedAtMinimum=true, чтобы UI показал честный label + пометку «(минимум — 1 мес)».</MEMENTO>
/**
 * Точный календарный срок контракта + тарифицируемый срок в месяцах.
 * Примеры: 01.01→01.07 = {6 мес, 0 дн}; 15.01→10.02 = {0, 26}; 15.01→16.02 = {1, 1};
 * 01.01→01.01 = {0, 0} → billableMonths = 1 (минимум). Невалидные даты / end<start → {1, 0, 1, false}.
 */
export function contractDuration(start: Date, end: Date): GbrDuration {
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) {
    return { months: 1, days: 0, billableMonths: 1, billedAtMinimum: false };
  }
  let whole = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  let boundary = addMonthsClamped(s, whole);
  if (boundary.getTime() > e.getTime()) {
    whole -= 1;
    boundary = addMonthsClamped(s, whole);
  }
  const days = Math.round((e.getTime() - boundary.getTime()) / 86400000);
  const rawMonths = round4(whole + days / 30.5);
  const billedAtMinimum = rawMonths < 1;
  return { months: whole, days, billableMonths: billedAtMinimum ? 1 : rawMonths, billedAtMinimum };
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return few;
  return many;
}

/**
 * Человекочитаемый срок ГБР: «15 мес 5 дн.», «1 мес», «12 мес», «26 дн.» (months=0).
 * Короткие формы: 1/2/5 мес; 1/2/5 дн. Если оба нуля — «1 мес» (минимальный срок).
 */
export function formatGbrDuration(months: number, days: number): string {
  const m = Math.max(0, Math.floor(months));
  const d = Math.max(0, Math.floor(days));
  if (m === 0 && d === 0) return "1 " + plural(1, "мес", "мес", "мес");
  const monthPart = m > 0 ? `${m} ${plural(m, "мес", "мес", "мес")}` : "";
  const dayPart = d > 0 ? `${d} ${plural(d, "дн.", "дн.", "дн.")}` : "";
  return [monthPart, dayPart].filter(Boolean).join(" ");
}

// <MEMENTO>2026-08-22: billableMonths хранится с 4 знаками (days/30.5), восстановление обратимо:
// months = floor(billable); days = round((billable - months)*30.5). months*30.5 дробное, поэтому
// totalDays через умножение НЕ считается (в отличие от делителя 30) — считаем остатком.
// Переполнение days > 30 (округление half-up на границе месяца) → months+1, days=0.
// Используется как fallback, когда дат контракта в сохранённых params нет (старые расчёты).</MEMENTO>
/**
 * Восстановление {months, days} из тарифицируемого срока billableMonths (делитель 30.5).
 */
export function gbrDurationFromBillable(billableMonths: number): { months: number; days: number } {
  const b = Number.isFinite(billableMonths) && billableMonths > 0 ? billableMonths : 1;
  let months = Math.floor(b);
  let days = Math.round((b - months) * 30.5);
  if (days > 30) {
    months += 1;
    days = 0;
  }
  return { months, days };
}
